import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  useUntypedBuildings, useRecordFacilityType, useResidenceTypes,
  type UntypedBuilding,
} from '../api/places.ts';
import { ApiError } from '../api/client.ts';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { formatDate } from '../dates.ts';

const secondary =
  'min-h-[44px] rounded-lg border border-neutral-300 px-3 text-sm font-semibold ' +
  'text-neutral-800 hover:bg-neutral-50 active:bg-neutral-100';
const primary =
  'min-h-[44px] rounded-lg bg-neutral-900 px-4 text-sm font-semibold text-white ' +
  'hover:bg-neutral-800 disabled:bg-neutral-200 disabled:text-neutral-600 disabled:cursor-not-allowed';

/**
 * Buildings with no type, for the Director of Social Work (061).
 *
 * **Why it exists.** A new building cannot be added without a type, but the
 * real-data import can bring one in, and an untyped building's clients are on
 * the Unknown rate: a wrong visiting quota that nothing else shows. So every
 * untyped building is here, busiest first, in one of two cases:
 *
 *  - **Conflicting types**: the legacy records disagree. Every competing type is
 *    shown with how often and where it was seen, so the reviewer chooses rather
 *    than guesses. Nursing against Hospital is usually the rehab boundary: a
 *    building with a rehab unit, recorded by the stay rather than the building.
 *  - **No type found**: nothing says what it is. Look it up.
 *
 * Its home is the Places tab of the admin section once that exists (see the
 * handover's proposal). It is a page of its own now because the import must not
 * land before an untyped building can be seen. The route decides who may open
 * it and who may record a type: tiers 1–2 and the two directors.
 */
export function UntypedBuildings() {
  const list = useUntypedBuildings();

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Buildings with no type</h1>
          <p className="mt-0.5 max-w-[60rem] text-sm text-neutral-500">
            A building's type sets how often everyone placed there must be visited. Until it is
            recorded, its clients are held to the rate for an unrecorded residence.
          </p>
        </header>
        <div className="page-body">
          {list.error && !list.data ? (
            list.error instanceof ApiError && list.error.status === 403 ? (
              <div role="alert" className="p-6">
                <h2 className="text-lg font-bold text-neutral-900">Not open to your roles</h2>
                <p className="mt-2 text-neutral-700">{list.error.message}</p>
                <Link to="/clients" className={`${primary} mt-5 inline-flex items-center`}>Back to clients</Link>
              </div>
            ) : (
              <LoadFailure what="The buildings with no type" error={list.error}
                           onRetry={() => void list.refetch()} retrying={list.isFetching} />
            )
          ) : !list.data ? (
            <p className="px-4 py-10 text-center text-neutral-600">Loading…</p>
          ) : list.data.facilities.length === 0 ? (
            <p className="px-4 py-10 text-center text-neutral-600">Every active building has a type.</p>
          ) : (
            <>
              <p className="px-4 pt-3 text-sm text-neutral-700 lg:px-5">
                {list.data.conflicting} with conflicting types, {list.data.no_type} with no type found.
                Busiest first.
              </p>
              <ul className="divide-y divide-neutral-100">
                {list.data.facilities.map((b) => <Building key={b.facility_id} b={b} />)}
              </ul>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Building({ b }: { b: UntypedBuilding }) {
  const types = useResidenceTypes('facility');
  const record = useRecordFacilityType();
  const [chosen, setChosen] = useState('');
  const homeTypes = b.candidates.filter((c) => !c.is_facility_type);

  return (
    <li className="px-4 py-4 lg:px-5">
      <p className="text-xs font-bold tracking-[0.1em] uppercase text-amber-800">{b.review_case}</p>
      <p className="mt-0.5 font-semibold text-neutral-900">{b.facility_name}</p>
      <p className="text-sm text-neutral-700">
        {[b.street_address, b.city].filter(Boolean).join(', ') || 'No address recorded'}
        {' · '}
        {b.clients_here === 0
          ? 'nobody lives here now'
          : `${b.clients_here} ${b.clients_here === 1 ? 'client lives' : 'clients live'} here now`}
      </p>

      {homeTypes.length > 0 && (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-sm text-neutral-800">
          Also recorded as {homeTypes.map((c) => c.label).join(' and ')}, which is a kind of home, not a
          building. This may be someone's home filed as a facility.
        </p>
      )}

      <fieldset className="mt-2">
        <legend className="text-sm font-semibold text-neutral-700">
          {b.review_case === 'Conflicting types' ? 'What the records say' : 'Nothing in the records says what it is'}
        </legend>
        <div className="mt-1 space-y-1">
          {b.candidates.filter((c) => c.is_facility_type).map((c) => (
            <label key={c.type_code}
                   className={`flex min-h-[44px] cursor-pointer gap-3 rounded-lg border px-3 py-2 ${
                     chosen === c.type_code ? 'border-neutral-900 bg-neutral-50' : 'border-neutral-200'}`}>
              <input type="radio" name={`type-${b.facility_id}`} checked={chosen === c.type_code}
                     onChange={() => setChosen(c.type_code)} className="mt-1 size-5 shrink-0" />
              <span>
                <span className="font-medium text-neutral-900">{c.label}</span>
                <span className="block text-sm text-neutral-600">
                  Seen {c.occurrences} {c.occurrences === 1 ? 'time' : 'times'} in {c.seen_in}
                  {c.first_seen && c.last_seen && `, ${formatDate(c.first_seen)} to ${formatDate(c.last_seen)}`}
                </span>
              </span>
            </label>
          ))}
          <label className="block">
            <span className="text-sm text-neutral-700">
              {b.candidates.some((c) => c.is_facility_type) ? 'Or something else:' : 'What it is:'}
            </span>
            <select
              className="mt-1 w-full min-h-[44px] rounded-lg border border-neutral-300 bg-white px-3"
              value={b.candidates.some((c) => c.type_code === chosen && c.is_facility_type) ? '' : chosen}
              onChange={(e) => setChosen(e.target.value)}
            >
              <option value="">Choose…</option>
              {(types.data?.residence_types ?? []).map((t) => (
                <option key={t.code} value={t.code}>{t.label}</option>
              ))}
            </select>
          </label>
        </div>
      </fieldset>

      <p className="mt-2 text-sm text-neutral-600">
        A building's type is not dated, so recording it sets the visiting standard for its clients'
        past periods as well as from now on.
      </p>

      {record.error && (
        <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-900">
          {record.error.message}
          {record.error instanceof ApiError && record.error.hint && (
            <span className="block text-rose-700">{record.error.hint}</span>
          )}
        </p>
      )}
      <button type="button" className={`${primary} mt-2`} disabled={!chosen || record.isPending}
              onClick={() => record.mutate({ facilityId: b.facility_id, facility_type: chosen })}>
        {record.isPending ? 'Recording…' : 'Record this type'}
      </button>
      {' '}
      {chosen && <button type="button" className={`${secondary} mt-2`} onClick={() => setChosen('')}>Clear</button>}
    </li>
  );
}
